import type {
    GetServerSideProps,
    NextComponentType,
    NextPageContext,
    PreviewData
} from 'next';
import type { ParsedUrlQuery } from 'querystring';
import type { NextProps } from './optimizely.types';

import nookies from 'nookies';
import {
    addOptimizelyProps,
    WithOptimizelyPageProps,
    DistributeWithOptimizelyPageProps
} from './OptimizelyProvider';
import { getOptimizelyState } from './getOptimizelyState';
import { getAnonId } from './getAnonIdFromContext';

type GetInitialPageProps<P, IP = P> = Required<
    NextComponentType<NextPageContext, IP, P>
>['getInitialProps'];
type PageCallback<P> = GetInitialPageProps<P>;

const getServerSideProps =
    <
        P extends NextProps = NextProps,
        Q extends ParsedUrlQuery = ParsedUrlQuery,
        D extends PreviewData = PreviewData
    >(
        callback: GetServerSideProps<P, Q, D>
    ): GetServerSideProps<DistributeWithOptimizelyPageProps<P>, Q, D> =>
    async (context) => {
        const initialProps = (callback && (await callback(context))) || {
            props: {}
        };

        /* get/generate anonId and Set-Cookie if it does not exist */
        const anonId = getAnonId(context);

        if ('redirect' in initialProps) {
            return initialProps;
        }

        if ('notFound' in initialProps) {
            return initialProps;
        }

        if ('props' in initialProps) {
            const [state, flushPromise] = await getOptimizelyState(
                {
                    url: context.req.url,
                    cookies: nookies.get(context),
                    headers: context.req.headers
                },
                anonId
            );
            await flushPromise;

            // props can be a promise https://github.com/vercel/next.js/pull/28607
            const isPromise =
                typeof initialProps.props === 'object' &&
                'then' in initialProps.props &&
                typeof initialProps.props.then === 'function';

            if (isPromise) {
                return {
                    props: (initialProps as { props: Promise<P> }).props.then(
                        (props) => addOptimizelyProps({ state }, props)
                    )
                };
            } else {
                return {
                    props: addOptimizelyProps(
                        { state },
                        (initialProps as { props: P }).props
                    )
                };
            }
        }
        return initialProps;
    };

/**
 * GIP can be called on the client & the server. The downside of this is that
 * any logic used here will be bundled into the page .js and possibly executed
 * on the client, inflating bundle size. We use the eval hack to work around
 * this: https://arunoda.me/blog/ssr-and-server-only-modules
 */
export const getInitialProps =
    <P = {}>( // eslint-disable-line @typescript-eslint/ban-types
        callback: PageCallback<P>
    ): GetInitialPageProps<P & WithOptimizelyPageProps> =>
    async (context) => {
        const props = (callback && (await callback(context))) || ({} as P);
        /* get/generate anonId and Set-Cookie or Document.cookie */
        const anonId = getAnonId(context);

        if (context.req) {
            const [state, flushPromise] = await getOptimizelyState(
                {
                    url: context.asPath,
                    cookies: nookies.get(context),
                    headers: context.req.headers
                },
                anonId
            );
            await flushPromise;
            return addOptimizelyProps({ state }, props);
        }

        let state = [];
        try {
            if (!context.asPath) throw new Error('no context.asPath');
            const res = await fetch(
                new URL(
                    '/api/experiments' + context.asPath,
                    window.location.origin
                ).toString(),
                // explicitly adding headers as it helps tests
                {
                    credentials: 'include',
                    headers: {
                        cookie: document.cookie,
                        'user-agent': window.navigator.userAgent
                    }
                }
            );
            if (res.ok) {
                const { state: retrievedState } = await res.json();
                state = retrievedState;
            }
        } catch (e) {
            // do nothing
        }
        return addOptimizelyProps({ state }, props);
    };

/**
 * An "empty" getServerSideProps function, that just enables Optimizely SSR
 */
export const _getServerSideProps = getServerSideProps(() =>
    Promise.resolve({ props: {} })
);

export default {
    getServerSideProps,
    getInitialProps
};
